import { useState, useRef, useEffect } from 'react'
import { apiClient } from '../lib/api'
import { track } from '../lib/analytics'
import { useBibleStore } from '../stores/bibleStore'
import type { BibleVerse as BibleVerseType } from '../lib/types'

interface Props {
  reference: string
}

interface CompareResult {
  translation: string
  text: string
}

export default function BibleVerse({ reference }: Props) {
  const [verse, setVerse] = useState<BibleVerseType | null>(null)
  const [comparing, setComparing] = useState(false)
  const [comparisons, setComparisons] = useState<CompareResult[]>([])
  const [expanded, setExpanded] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const [contentHeight, setContentHeight] = useState(0)

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight)
    }
  }, [expanded, verse, comparing, comparisons])

  const handleTap = async () => {
    if (loading) return

    if (expanded) {
      setExpanded(false)
      setComparing(false)
      return
    }

    if (verse) {
      setExpanded(true)
      return
    }

    setLoading(true)
    setError(null)
    track('bible_verse_tapped', { reference })
    try {
      const data = await apiClient.get<BibleVerseType>(
        `bible/verse/?ref=${encodeURIComponent(reference)}`
      )
      setVerse(data)
      setExpanded(true)
    } catch {
      setError('Could not load verse')
      track('bible_verse_error_shown', { reference })
    } finally {
      setLoading(false)
    }
  }

  const handleCompare = async () => {
    if (comparing) {
      setComparing(false)
      return
    }

    if (comparisons.length > 0) {
      setComparing(true)
      return
    }

    setLoading(true)
    track('bible_verse_compared', { reference })
    try {
      const data = await apiClient.get<CompareResult[]>(
        `bible/compare/?ref=${encodeURIComponent(reference)}`
      )
      setComparisons(data)
      setComparing(true)
    } catch {
      setError('Could not load comparisons')
      track('bible_verse_error_shown', { reference, kind: 'compare' })
    } finally {
      setLoading(false)
    }
  }

  const handleCopy = async () => {
    if (!verse) return
    const text = `${verse.reference} - ${verse.text} (${verse.translation})`
    await navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <span className="inline">
      <button
        onClick={handleTap}
        className="text-accent underline decoration-dotted underline-offset-2 hover:text-accent-hover transition-colors"
        disabled={loading}
      >
        {reference}
        {loading && <span className="ml-1 inline-block w-3 h-3 border-2 border-accent border-t-transparent rounded-full animate-spin align-middle" />}
      </button>

      {error && (
        <button
          onClick={() => { setError(null); handleTap() }}
          className="text-error text-xs ml-1 hover:underline"
        >
          {error} — tap to retry
        </button>
      )}

      <div
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{ maxHeight: expanded && verse ? contentHeight : 0 }}
      >
        <div ref={contentRef}>
          {verse && (
            <div className="block mt-2 mb-2 bg-elevated rounded-lg p-3 border border-surface">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-accent">{verse.reference}</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-text-muted">{verse.translation}</span>
                  <button
                    onClick={handleCopy}
                    className="text-text-muted hover:text-text-secondary transition-colors"
                    aria-label="Copy verse"
                  >
                    {copied ? (
                      <svg className="w-3.5 h-3.5 text-accent" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                    ) : (
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9.75a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              <p className="text-sm text-text-primary leading-relaxed">{verse.text}</p>

              <div className="flex items-center gap-3 mt-2">
                <button
                  onClick={handleCompare}
                  className="text-xs text-text-secondary hover:text-accent transition-colors"
                >
                  {comparing ? 'Hide translations' : 'Compare translations'}
                </button>
                <button
                  onClick={() => useBibleStore.getState().openReaderPanel(reference, verse?.translation)}
                  className="text-xs text-text-secondary hover:text-accent transition-colors flex items-center gap-1"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                  </svg>
                  Read in context
                </button>
              </div>

              {comparing && comparisons.length > 0 && (
                <div className="mt-2 space-y-2 border-t border-surface pt-2">
                  {comparisons.map((c) => (
                    <div key={c.translation}>
                      <span className="text-xs font-medium text-text-muted">{c.translation}</span>
                      <p className="text-sm text-text-secondary leading-relaxed">{c.text}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </span>
  )
}
